import React from "react";
import { shuffleGameData } from "../../lib/game-helpers";
import GameGrid from "../GameGrid";
import NumberOfMistakesDisplay from "../NumberOfMistakesDisplay";
import GameLostModal from "../modals/GameLostModal";
import GameWonModal from "../modals/GameWonModal";
import { AlertCircle, CalendarDays, Hash, Target } from "lucide-react";
import { format, parseISO } from "date-fns";

import { Separator } from "../ui/separator";
import ConfettiExplosion from "react-confetti-explosion";

import { PuzzleDataContext } from "../../providers/PuzzleDataProvider";
import { GameStatusContext } from "../../providers/GameStatusProvider";
import GameControlButtonsPanel from "../GameControlButtonsPanel";

import ViewResultsModal from "../modals/ViewResultsModal";
import GameStatsCard from "../GameStatsCard/GameStatsCard";

import Sponsorship, { SponsorshipLinks } from "../Sponsorship";


function Game() {
  const { gameData, categorySize, numCategories, puzzleDate, puzzleIndex } =
    React.useContext(PuzzleDataContext);
  const { submittedGuesses, solvedGameData, isGameOver, isGameWon } =
    React.useContext(GameStatusContext);

  const [shuffledRows, setShuffledRows] = React.useState(
    shuffleGameData({ gameData })
  );
  const [isEndGameModalOpen, setisEndGameModalOpen] = React.useState(false);
  const [shouldGridShake, setShouldGridShake] = React.useState(false);
  const [showConfetti, setShowConfetti] = React.useState(false);

  // use effect to update Game Grid after a row has been correctly solved
  React.useEffect(() => {
    const categoriesToRemoveFromRows = solvedGameData.map(
      (data) => data.category
    );
    const dataLeftForRows = gameData.filter((data) => {
      return !categoriesToRemoveFromRows.includes(data.category);
    });
    if (dataLeftForRows.length > 0) {
      setShuffledRows(shuffleGameData({ gameData: dataLeftForRows }));
    }
  }, [solvedGameData]);

  // Handle End Game!
  React.useEffect(() => {
    if (!isGameOver) {
      return;
    }
    // extra delay for game won to allow confetti to show
    const modalDelay = isGameWon ? 2000 : 250;
    const delayModalOpen = window.setTimeout(() => {
      setisEndGameModalOpen(true);
      //unmount confetti after modal opens
      setShowConfetti(false);
    }, modalDelay);

    if (isGameWon) {
      setShowConfetti(true);
    }

    return () => window.clearTimeout(delayModalOpen);
  }, [isGameOver]);

  const formattedPuzzleDate = format(parseISO(puzzleDate), "MMM d, yyyy");

  return (
    <>
      <div className="mt-4 w-full max-w-[500px] px-4 mx-auto">
        <div className="rounded-xl border border-border/80 bg-card/85 shadow-[0_8px_30px_rgba(15,23,42,0.08)] px-4 py-3 flex flex-col sm:flex-row items-center sm:justify-between gap-3 text-center backdrop-blur-sm">
          <div className="flex items-center justify-center gap-2">
            <Target className="h-4 w-4 text-primary/80" />
            <h3 className="text-sm sm:text-base font-semibold tracking-tight text-center w-full sm:w-auto">
              Create {numCategories} groups of {categorySize}
            </h3>
          </div>
          <div className="hidden sm:block h-5 w-px bg-border/80" />
          <div className="flex items-center justify-center gap-2">
            <AlertCircle className="h-4 w-4 text-primary/80" />
            <NumberOfMistakesDisplay compact />
          </div>
        </div>
        <div className="mt-2 flex items-center justify-center gap-4 text-xs sm:text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Hash className="h-3.5 w-3.5" />
            Puzzle {puzzleIndex}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays className="h-3.5 w-3.5" />
            {formattedPuzzleDate}
          </span>
        </div>
      </div>

      <div className={`game-wrapper ${!isGameOver ? "pb-[calc(7rem+env(safe-area-inset-bottom))] md:pb-0" : ""}`}>
        {isGameOver && isGameWon ? (
          <GameWonModal
            open={isEndGameModalOpen}
            submittedGuesses={submittedGuesses}
          />
        ) : (
          <GameLostModal
            open={isEndGameModalOpen}
            submittedGuesses={submittedGuesses}
          />
        )}
        <GameGrid
          gameRows={shuffledRows}
          shouldGridShake={shouldGridShake}
          setShouldGridShake={setShouldGridShake}
        />
        {showConfetti && isGameOver && (
          <div className="grid place-content-center">
            <ConfettiExplosion
              particleCount={100}
              force={0.8}
              duration={2500}
            />
          </div>
        )}
        <Separator />

        {!isGameOver ? (
          <>
            <GameControlButtonsPanel
              shuffledRows={shuffledRows}
              setShuffledRows={setShuffledRows}
              setGridShake={setShouldGridShake}
            />
          </>
        ) : (
          <ViewResultsModal />
        )}
        <Sponsorship />
        <GameStatsCard />
        <SponsorshipLinks />
      </div>
    </>
  );
}

export default Game;
